import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class BulkOrderForm extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit, { signal });
    window.addEventListener('scroll', this.#updateSummaryVisibility, { signal, passive: true });
    window.addEventListener('resize', this.#updateSummaryVisibility, { signal });
    this.#updateSummaryVisibility();
    this.#updateSubmitState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #updateSummaryVisibility = () => {
    const bounds = this.getBoundingClientRect();
    this.classList.toggle('bulk-order-form--summary-visible', bounds.bottom > 0 && bounds.top < window.innerHeight);
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-quantity-action]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const input = button.parentElement?.querySelector('[data-bulk-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    event.preventDefault();

    const current = Number(input.value) || 0;
    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.dataset.increment) || 1;
    const maximum = input.max ? Number(input.max) : null;
    let next = current;

    if (button.dataset.quantityAction === 'increase') {
      next = current === 0 ? minimum : current + increment;
      if (maximum !== null) next = Math.min(next, maximum);
    } else {
      next = current <= minimum ? 0 : current - increment;
    }

    input.value = String(Math.max(0, next));
    this.#validateInput(input);
    this.#updateSubmitState();
  };

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-bulk-quantity]')) return;

    event.target.setCustomValidity('');
    this.#setStatus('');
    this.#updateSubmitState();
  };

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-bulk-quantity]')) return;

    this.#validateInput(event.target);
    event.target.reportValidity();
    this.#updateSubmitState();
  };

  #handleSubmit = async (event) => {
    event.preventDefault();

    const inputs = this.#quantityInputs;
    const invalidInput = inputs.find((input) => !this.#validateInput(input));
    if (invalidInput) {
      invalidInput.reportValidity();
      invalidInput.focus();
      return;
    }

    const items = inputs
      .map((input) => ({ id: Number(input.dataset.variantId), quantity: Number(input.value) || 0 }))
      .filter((item) => item.id && item.quantity > 0);

    if (!items.length) {
      this.#setStatus(this.dataset.emptyMessage, true);
      return;
    }

    const submitButton = this.querySelector('[data-bulk-submit]');
    if (!(submitButton instanceof HTMLButtonElement)) return;

    const originalLabel = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = this.dataset.loadingLabel;
    this.#setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
        body: JSON.stringify({
          items,
          sections: [...new Set(sectionIds)],
          sections_url: window.location.pathname,
        }),
      }));
      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || this.dataset.errorMessage);
      }

      this.dispatchEvent(new CartAddEvent(payload, this.id, {
        source: 'product-form-component',
        itemCount: items.reduce((total, item) => total + item.quantity, 0),
        sections: payload.sections,
      }));

      document.querySelector('cart-drawer-component')?.open();
      inputs.forEach((input) => {
        input.value = '0';
        input.setCustomValidity('');
      });
      this.#setStatus(this.dataset.successMessage);
    } catch (error) {
      const message = error instanceof Error ? error.message : this.dataset.errorMessage;
      this.#setStatus(message, true);
    } finally {
      submitButton.textContent = originalLabel || this.dataset.submitLabel;
      this.#updateSubmitState();
    }
  };

  #validateInput(input) {
    const quantity = Number(input.value) || 0;
    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.dataset.increment) || 1;
    const maximum = input.max ? Number(input.max) : null;
    let message = '';

    if (quantity < 0 || !Number.isInteger(quantity)) {
      message = this.dataset.wholeNumberMessage;
    } else if (quantity > 0 && quantity < minimum) {
      message = input.dataset.minimumMessage;
    } else if (quantity > 0 && (quantity - minimum) % increment !== 0) {
      message = input.dataset.incrementMessage;
    } else if (maximum !== null && quantity > maximum) {
      message = input.dataset.maximumMessage;
    }

    input.setCustomValidity(message);
    return message === '';
  }

  #updateSubmitState() {
    const submitButton = this.querySelector('[data-bulk-submit]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !this.#quantityInputs.some((input) => Number(input.value) > 0);
    }

    this.#updateLineTotals();
  }

  #updateLineTotals() {
    let subtotal = 0;
    let totalUnits = 0;

    this.#quantityInputs.forEach((input) => {
      const quantity = Number(input.value) || 0;
      const unitPrice = this.#unitPriceForQuantity(input, quantity);
      const total = Math.max(0, quantity) * unitPrice;
      totalUnits += Math.max(0, quantity);
      subtotal += total;
      const totalElement = input.closest('.bulk-order-form__row')?.querySelector('[data-bulk-line-total]');
      if (!(totalElement instanceof HTMLElement)) return;

      const previousTotal = totalElement.dataset.totalCents;
      totalElement.textContent = this.#formatMoney(total);
      totalElement.dataset.totalCents = String(total);

      if (previousTotal !== undefined && previousTotal !== String(total)) {
        totalElement.classList.remove('bulk-order-form__price--updating');
        void totalElement.offsetWidth;
        totalElement.classList.add('bulk-order-form__price--updating');
      }
    });

    const totalUnitsElement = this.querySelector('[data-bulk-total-units]');
    if (totalUnitsElement instanceof HTMLElement) {
      totalUnitsElement.textContent = new Intl.NumberFormat(this.dataset.locale || 'en-US').format(totalUnits);
    }

    const subtotalElement = this.querySelector('[data-bulk-subtotal]');
    if (!(subtotalElement instanceof HTMLElement)) return;

    const previousSubtotal = subtotalElement.dataset.totalCents;
    subtotalElement.textContent = this.#formatMoney(subtotal);
    subtotalElement.dataset.totalCents = String(subtotal);

    if (previousSubtotal !== undefined && previousSubtotal !== String(subtotal)) {
      subtotalElement.classList.remove('bulk-order-form__price--updating');
      void subtotalElement.offsetWidth;
      subtotalElement.classList.add('bulk-order-form__price--updating');
    }
  }

  #unitPriceForQuantity(input, quantity) {
    let unitPrice = Number(input.dataset.unitPrice) || 0;

    for (const priceBreak of (input.dataset.priceBreaks || '').split(',')) {
      const [minimum, price] = priceBreak.split(':').map(Number);
      if (Number.isFinite(minimum) && Number.isFinite(price) && quantity >= minimum) {
        unitPrice = price;
      }
    }

    return unitPrice;
  }

  #formatMoney(cents) {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || 'en-US';
    const amount = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);

    return `${amount} ${currency}`;
  }

  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-bulk-status]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = message;
    status.classList.toggle('bulk-order-form__status--error', isError);
  }

  get #quantityInputs() {
    return Array.from(this.querySelectorAll('[data-bulk-quantity]')).filter(
      (input) => input instanceof HTMLInputElement && !input.disabled
    );
  }
}

if (!customElements.get('bulk-order-form')) {
  customElements.define('bulk-order-form', BulkOrderForm);
}
